import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { departmentsApi, extractList } from '../../api/facades';

const DepartmentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = React.useState<number | null>(null);

  const { data: res, isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => departmentsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setDeleteId(null);
      toast.success('Department deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'department_name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'is_active', label: 'Active', render: (r: any) => (r.is_active === 'Yes' ? 'Yes' : 'No') },
  ];

  return (
    <div>
      <PageHeader title="Departments" subtitle="Manage departments." primaryAction={{ label: 'New Department', to: '/dashboard/settings/departments/new' }} />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <DataTable columns={columns} data={rows} loading={isLoading} emptyMessage="No departments." onRowClick={(id) => navigate(`/dashboard/settings/departments/${id}`)} onEdit={(id) => navigate(`/dashboard/settings/departments/${id}/edit`)} onDelete={(id) => setDeleteId(Number(id))} />
      </div>
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)} title="Delete department" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default DepartmentsListPage;
